import { BrandApplicationStatus, BrandCampaignStatus, BrandDealStatus, BrandListingStatus } from '../api/payment/brand-deal.api';

// Hàm thuần cho "Hợp tác quảng cáo" (Brand Deal) — nhãn trạng thái, net sau phí, chèn link vào
// caption, số ngày còn lại. Tách khỏi component để test không phải dựng React.

/** net = floor(price * (100 - feePercent) / 100) — ĐÚNG công thức payment dùng để giải ngân. */
export const netAfterFee = (price: number, feePercent: number): number =>
  Math.floor((price * (100 - feePercent)) / 100);

/** Trạng thái 1 deal (lời mời) ⇒ nhãn tiếng Việt hiện cho creator/doanh nghiệp/admin. */
export const dealStatusLabel = (s: BrandDealStatus): string => {
  switch (s) {
    case 'Invited':
      return 'Đã mời';
    case 'Publishing':
      return 'Đang đăng';
    case 'Live':
      return 'Đang chạy';
    case 'Completed':
      return 'Hoàn thành';
    case 'Declined':
      return 'Đã từ chối';
    case 'Expired':
      return 'Hết hạn';
    case 'Cancelled':
      return 'Đã huỷ';
    case 'Forfeited':
      return 'Kết thúc do gỡ bài';
    case 'PublishFailed':
      return 'Đăng lỗi';
    case 'NeedsReview':
      return 'Chờ admin xem xét';
    case 'AwaitingPublish':
      return 'Chờ creator đăng';
    default:
      return s;
  }
};

/** Trạng thái 1 chiến dịch ⇒ nhãn tiếng Việt hiện cho doanh nghiệp/admin. */
export const campaignStatusLabel = (s: BrandCampaignStatus): string => {
  switch (s) {
    case 'Draft':
      return 'Nháp';
    case 'PendingReview':
      return 'Chờ duyệt';
    case 'Approved':
      return 'Đã duyệt';
    case 'Rejected':
      return 'Bị từ chối';
    case 'Closed':
      return 'Đã đóng';
    default:
      return s;
  }
};

/**
 * Chèn `link` vào mọi chỗ `{link}` xuất hiện trong caption; caption không có `{link}` thì gắn
 * thêm 1 dòng trống + link ở cuối. CÙNG công thức với `BrandDealRules.RenderCaptionWithLink`
 * phía payment — bản xem trước creator thấy phải khớp bài thật đăng lên.
 */
export const renderCaption = (caption: string, link: string): string =>
  caption.includes('{link}') ? caption.split('{link}').join(link) : `${caption}\n\n${link}`;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Số ngày còn lại tới `endsAtIso`, làm tròn lên, tối thiểu 0 (đã qua hạn ⇒ 0). */
export const daysLeft = (endsAtIso: string, nowMs: number): number => {
  const ends = Date.parse(endsAtIso);
  if (!Number.isFinite(ends)) return 0;
  return Math.max(0, Math.ceil((ends - nowMs) / MS_PER_DAY));
};

// ─────────── Validate form chiến dịch (doanh nghiệp) — khớp CHÍNH XÁC thứ tự + câu lỗi phía payment
// (BrandDealRules.ValidateCampaign + BrandDealConst) để không báo lỗi khác với backend khi submit. ───

export const BRAND_CAMPAIGN_LIMITS = {
  titleMaxLength: 120,
  captionMaxLength: 2000,
  destinationUrlMaxLength: 500,
  minHoldDays: 7,
  maxHoldDays: 30,
  maxImages: 10,
  maxVideos: 1,
} as const;

/** M-3: URL https tuyệt đối thật sự (có host), không chỉ "bắt đầu bằng https://". Export để
 * `validateBriefForm` (M3, src/utils/brand-creative.ts) dùng lại — cùng 1 chỗ kiểm định dạng URL. */
export const isValidHttpsUrl = (url: string): boolean => {
  if (!url.startsWith('https://')) return false;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && u.hostname.length > 0;
  } catch {
    return false;
  }
};

export interface BrandCampaignFormInput {
  title: string;
  caption: string;
  destinationUrl: string;
  holdDays: number;
  images: number;
  videos: number;
  /** Bài viết không bắt buộc ảnh/video (khớp BrandDealRules.ValidateCampaign). */
  contentFormat?: string | null;
}

/**
 * Kiểm nội dung form chiến dịch — trả `null` khi hợp lệ, ngược lại câu lỗi tiếng Việt đầu tiên vi
 * phạm, ĐÚNG thứ tự + ĐÚNG câu chữ với `BrandDealRules.ValidateCampaign` phía payment (title → caption
 * → link đích → số ngày giữ bài → số ảnh/video) — chặn sớm ở FE, tránh round-trip lỗi 400 vô ích.
 */
export const validateBrandCampaignForm = (input: BrandCampaignFormInput): string | null => {
  const { titleMaxLength, captionMaxLength, destinationUrlMaxLength, minHoldDays, maxHoldDays, maxImages, maxVideos } =
    BRAND_CAMPAIGN_LIMITS;

  // T2-b (fix-before-merge): rỗng phải báo "chưa nhập", không dùng chung câu "tối đa N ký tự" với lỗi
  // quá dài (dễ hiểu lầm) — khớp BrandDealRules.ValidateCampaign phía payment.
  if (!input.title.trim()) return 'Bạn chưa nhập tiêu đề.';
  if (input.title.length > titleMaxLength) return `Tiêu đề tối đa ${titleMaxLength} ký tự.`;

  if (!input.caption.trim()) return 'Bạn chưa nhập nội dung.';
  if (input.caption.length > captionMaxLength) return `Nội dung tối đa ${captionMaxLength} ký tự.`;

  // M-3: StartsWith("https://") không đủ — "https://" hay "https://a b" cũng qua được rồi 500 ở BE khi
  // dựng URL thật (new URL(...) ném lỗi) — kiểm bằng URL() + scheme https + có host, khớp BE.
  if (!input.destinationUrl.trim() || !isValidHttpsUrl(input.destinationUrl))
    return 'Link đích phải bắt đầu bằng https://.';
  if (input.destinationUrl.length > destinationUrlMaxLength)
    return `Link đích tối đa ${destinationUrlMaxLength} ký tự.`;

  if (input.holdDays < minHoldDays || input.holdDays > maxHoldDays)
    return `Thời gian giữ bài từ ${minHoldDays} đến ${maxHoldDays} ngày.`;

  if (input.images <= 0 && input.videos <= 0 && input.contentFormat !== 'Article') return 'Cần ít nhất 1 ảnh hoặc video.';
  if (input.images > maxImages) return `Tối đa ${maxImages} ảnh.`;
  if (input.videos > maxVideos) return `Tối đa ${maxVideos} video.`;

  return null;
};

/**
 * Kiểm giá mời 1 deal — câu lỗi ĐÚNG với payment ("Giá deal từ 100.000đ đến 100.000.000đ.").
 * `minPriceVnd` lấy từ SystemConfig `brandDeal.minPriceVnd` (mặc định 100.000, admin có thể đổi).
 */
export const DEFAULT_DEAL_LIMITS = { minPriceVnd: 100000, maxPriceVnd: 100000000 } as const;

/** "100.000đ – 100.000.000đ" cho nhãn ô giá. */
export const priceRangeLabel = (minPriceVnd: number, maxPriceVnd: number = DEFAULT_DEAL_LIMITS.maxPriceVnd): string =>
  `${minPriceVnd.toLocaleString('vi-VN')}đ – ${maxPriceVnd.toLocaleString('vi-VN')}đ`;

export const validateDealPrice = (priceVnd: number, minPriceVnd = 100000, maxPriceVnd = 100000000): string | null => {
  if (!Number.isFinite(priceVnd) || priceVnd < minPriceVnd || priceVnd > maxPriceVnd)
    return `Giá deal từ ${minPriceVnd.toLocaleString('vi-VN')}đ đến ${maxPriceVnd.toLocaleString('vi-VN')}đ.`;
  return null;
};

// ─────────── M2: sàn mở cho creator xin tham gia ───────────

/** Số suất còn lại = tổng − đã dùng, không bao giờ âm (hết suất ⇒ 0, không phải số âm). */
export const slotsLeft = (total: number, used: number): number => Math.max(0, total - used);

/** Trạng thái 1 đơn xin tham gia sàn ⇒ nhãn tiếng Việt (creator/doanh nghiệp). */
export const applicationStatusLabel = (s: BrandApplicationStatus): string => {
  switch (s) {
    case 'Pending':
      return 'Chờ duyệt';
    case 'Approved':
      return 'Được duyệt';
    case 'Rejected':
      return 'Bị từ chối';
    case 'Withdrawn':
      return 'Đã rút';
    case 'Expired':
      return 'Hết hạn';
    case 'Closed':
      return 'Đã đóng';
    default:
      return s;
  }
};

/** Trạng thái đăng sàn 1 chiến dịch ⇒ nhãn tiếng Việt (doanh nghiệp/admin). */
export const listingStatusLabel = (s: BrandListingStatus): string => {
  switch (s) {
    case 'None':
      return 'Chưa đăng';
    case 'Open':
      return 'Đang mở';
    case 'Closed':
      return 'Đã đóng';
    default:
      return s;
  }
};

export const BRAND_LISTING_LIMITS = {
  maxPriceVnd: 100000000,
  minSlots: 1,
  maxSlots: 100,
  minApplyDays: 1,
  maxApplyDays: 30,
  defaultApplyDays: 14,
} as const;

export interface BrandListingFormInput {
  slotPriceVnd: number;
  slotsTotal: number;
  applyDays: number;
}

/**
 * Kiểm tham số đăng sàn — trả `null` khi hợp lệ, ngược lại câu lỗi tiếng Việt đầu tiên vi phạm, ĐÚNG
 * thứ tự + ĐÚNG câu chữ với `BrandListing.ValidateListing` phía payment (giá mỗi suất → số suất → hạn
 * nhận đơn). Câu lỗi giá chèn đúng `minPriceVnd` đang áp dụng, khớp BrandDealConst.SlotPriceRangeMessage.
 */
export const validateListingForm = (input: BrandListingFormInput, minPriceVnd = 100000): string | null => {
  const { maxPriceVnd, minSlots, maxSlots, minApplyDays, maxApplyDays } = BRAND_LISTING_LIMITS;
  const priceError = `Giá mỗi suất từ ${minPriceVnd.toLocaleString('vi-VN')}đ đến ${maxPriceVnd.toLocaleString('vi-VN')}đ.`;

  if (!Number.isFinite(input.slotPriceVnd)) return priceError;
  // M-6 (final review): input number cho phép gõ thập phân (vd 500000.5) — binding `long` phía BE thì
  // trả 400 không có `message`, FE chỉ hiện chung chung "Đăng lên sàn thất bại." Chặn sớm ở đây, TRƯỚC
  // kiểm khoảng giá trị (1 giá trị thập phân vẫn có thể nằm trong khoảng 100.000–100.000.000).
  if (!Number.isInteger(input.slotPriceVnd)) return 'Giá mỗi suất phải là số nguyên.';
  if (input.slotPriceVnd < minPriceVnd || input.slotPriceVnd > maxPriceVnd)
    return priceError;
  if (!Number.isInteger(input.slotsTotal) || input.slotsTotal < minSlots || input.slotsTotal > maxSlots)
    return 'Số suất từ 1 đến 100.';
  if (!Number.isInteger(input.applyDays) || input.applyDays < minApplyDays || input.applyDays > maxApplyDays)
    return 'Hạn nhận đơn từ 1 đến 30 ngày.';

  return null;
};
